<template>
    <WithSideNavigation :has-left-menu="hasLeftMenu"
                        :enable-container="isContainerEnabled">
        <template #left>
            <section>
                <SideNavigation :navigation-items="navigationItems"/>
            </section>
        </template>
        <template #main>
            <section class="page">
                <transition appear
                            mode="out-in"
                            enter-active-class="animated fadeIn u-anim-dur-350"
                            leave-active-class="animated fadeOut u-anim-dur-250">
                    <template v-if="page">
                        <component :is="page.component"
                                   :key="key"
                                   :content="content"
                                   :has-left-menu="hasLeftMenu"
                                   :alias="alias"/>
                    </template>
                </transition>
            </section>
        </template>
    </WithSideNavigation>
</template>

<script setup lang="ts">
import { computed, ref, shallowRef, onMounted } from 'vue';
import { ClientComponent, pageResolver } from '@/core/app/componentResolver.service';
import appStore from '@/core/app/app.store';
import { NavigationItem, PageData } from '@/types/contentServerContract';
import contentApi from '@/core/http/contentApi.service';
import { HttpCancelError } from '@/core/http/HttpCancelError';
import domService from '@/core/dom/dom.service';
import HttpStatus from 'http-status-codes';
import botService from '@/project/site/bot.service';
import serverContext from '@/core/serverContext.service';
import urlStringHelperService from '@/core/urlStringHelper.service';
import umbracoContentError from '@/core/umbracoContentError.service';
import gtmService from '@/project/tracking/gtm/GTM.service';
import themeService from '@/project/app/themes/theme.service';
import scrollService from '@/core/scroll/scroll.service';
import WithSideNavigation from '@/project/layout/navigation/WithSideNavigation.vue';
import SideNavigation from '@/project/layout/navigation/SideNavigation.vue';

const PAGE_NAME = 'error';
const pageData = ref<PageData | null>(null);
const page = shallowRef<ClientComponent | null>(null);

const key = computed((): string => {
    return pageData.value?.metadata?.key ?? Date.now().toString();
});

const alias = computed((): string | null => {
    return pageData.value?.jsonContent?.alias ?? null;
});

const content = computed((): any | null => {
    return pageData.value?.jsonContent?.content ?? null;
});

const isTakeOverEnabled = computed(() => {
    return themeService.isTakeOverEnabled;
});

const isContainerEnabled = computed((): boolean => {
    return isTakeOverEnabled.value === false && appStore.isContainerDisabled === false;
});

const hasLeftMenu = computed(() => {
    return navigationItems.value.length > 0;
});

const navigationItems = computed((): NavigationItem[] => {
    return pageData.value?.navigation.leftMenu ?? [];
});

function renderErrorPage() {
    if (!pageData.value) return;

    const resolvedAlias = pageData.value.jsonContent?.alias;

    if (resolvedAlias) {
        const resolved = pageResolver.resolve(resolvedAlias);
        if (resolved) {
            page.value = resolved;
        } else {
            pageData.value = umbracoContentError.umbracoFailedContent();
            page.value = pageResolver.resolve(pageData.value.jsonContent!.alias ?? 'contentPage');
        }
    } else {
        page.value = null;
    }

    appStore.initPage({ breadcrumb: pageData.value.navigation.breadcrumb });
    domService.updateMetaData(pageData.value.metadata, true);

    if (botService.uaIsPrerender()) {
        domService.setPrerenderStatusCode(appStore.httpStatusCode ?? HttpStatus.NOT_FOUND);
    }

    if (pageData.value.metadata?.id) {
        gtmService.trackPageRendered({ pageType: PAGE_NAME, pageId: pageData.value.metadata.id });
        gtmService.trackGenericPageView(PAGE_NAME, pageData.value.metadata.id);
    }
}

function getErrorPagePath(): string {
    const productWid = appStore.notFoundProductWid;

    if (productWid && serverContext.settings.product404Page) {
        return urlStringHelperService.withoutCulturePrefix(`${serverContext.settings.product404Page}?wid=${productWid}`);
    }

    return '/404';
}

async function loadErrorPage() {
    const path = getErrorPagePath();

    try {
        pageData.value = await contentApi.getPage(path);
        renderErrorPage();
    } catch (error: any) {
        if (error instanceof HttpCancelError) {
            return;
        }

        pageData.value = umbracoContentError.umbracoFailedContent();
        renderErrorPage();
    }
}

onMounted(() => {
    scrollService.scrollToTop();
    loadErrorPage();
});
</script>
